import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Segmented from "@/components/enhance/Segmented";
import { KEYS, PRESETS, polishEnhancement } from "@/lib/enhanceApi";
import { safeApiError } from "@/lib/safeErrorText";

const NO_KEY = "none";

function FxSlider({ label, value, onChange, testId, disabled }) {
  return (
    <div>
      <div className="mb-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">
        <span>{label}</span><span data-testid={`${testId}-value`} className="text-amber-200">{value}%</span>
      </div>
      <input type="range" data-testid={testId} aria-label={label} value={value} min={0} max={100} step={1} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))} className="h-1.5 w-full cursor-pointer accent-amber-300 disabled:opacity-40" />
    </div>
  );
}

function KeyPicker({ value, analysis, onChange, disabled }) {
  const detected = analysis?.key;
  return (
    <div>
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Song key for pitch correction</div>
      <Select value={value || NO_KEY} onValueChange={(v) => onChange(v === NO_KEY ? null : v)} disabled={disabled}>
        <SelectTrigger data-testid="polish-key" className="rounded-xl border-white/10 bg-black/40 text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={NO_KEY}>Every note (no key)</SelectItem>
          {KEYS.map((k) => <SelectItem key={k} value={k}>{k}{k === detected ? " · detected" : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <p data-testid="polish-key-detected" className="mt-1.5 text-[11px] text-white/45">
        {detected ? `We heard ${detected} (${Math.round((analysis.key_confidence || 0) * 100)}% sure)${analysis.range ? ` · your range ${analysis.range}` : ""}.` : "We couldn't hear a clear key — every note is used."}
      </p>
    </div>
  );
}

/** Studio-Polished sound: a ready-made preset plus simple controls, then re-make only that version. */
export default function PolishPanel({ item, onStarted }) {
  const saved = useMemo(() => ({ settings: item.settings, key: item.key ?? null }), [item.settings, item.key]);
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(saved); }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!draft.settings) return null;
  const s = draft.settings;
  const running = item.status === "running";
  const changed = JSON.stringify(draft) !== JSON.stringify(saved);
  const set = (patch) => setDraft((d) => ({ ...d, settings: { ...d.settings, ...patch } }));
  const preset = PRESETS.find((p) => Object.entries(p.s).every(([k, v]) => s[k] === v))?.key;
  const apply = async () => {
    setBusy(true);
    try { onStarted(await polishEnhancement(item.id, draft)); toast.success("Re-making the Studio-Polished version…"); }
    catch (e) { toast.error("Couldn't start", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="polish-panel" className="rounded-3xl border border-amber-300/15 bg-[#0d0c14]/80 p-5 sm:p-6">
      <h2 className="font-display text-base font-semibold md:text-lg">Studio-Polished sound</h2>
      <p className="mt-1 text-[13px] text-white/55">Pick a ready-made sound, fine-tune it, then re-make the polished version. Your voice and accent stay yours.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button key={p.key} type="button" data-testid={`polish-preset-${p.key}`} disabled={running} onClick={() => set(p.s)}
            className={`rounded-full border px-3.5 py-1.5 text-[12px] font-medium transition-colors ${preset === p.key ? "border-amber-300 bg-amber-300/15 text-amber-100" : "border-white/10 text-white/65 hover:border-white/25 hover:text-white"}`}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <KeyPicker value={draft.key} analysis={item.analysis} disabled={running} onChange={(key) => setDraft((d) => ({ ...d, key }))} />
        <Segmented label="Pitch correction" testId="polish-tune" value={s.tune} disabled={running} onChange={(tune) => set({ tune })} options={[["off", "Off"], ["natural", "Gentle"], ["tight", "Tight"]]} />
        <Segmented label="Tone" testId="polish-eq" value={s.eq} disabled={running} onChange={(eq) => set({ eq })} options={[["warm", "Warm"], ["balanced", "Balanced"], ["bright", "Bright"]]} />
        <Segmented label="Evening out (compression)" testId="polish-comp" value={s.compression} disabled={running} onChange={(compression) => set({ compression })} options={[["light", "Light"], ["medium", "Medium"], ["strong", "Strong"]]} />
        <Segmented label="Background noise clean-up" testId="polish-noise" value={s.noise} disabled={running} onChange={(noise) => set({ noise })} options={[["off", "Off"], ["light", "Light"], ["strong", "Strong"]]} />
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
          <span><span className="block text-[13px] font-medium">Vocal doubling</span><span className="block text-[11px] text-white/45">Two soft copies, left and right</span></span>
          <Switch data-testid="polish-doubling" checked={s.doubling} disabled={running} onCheckedChange={(doubling) => set({ doubling })} />
        </label>
        <FxSlider label="Reverb (room)" testId="polish-reverb" value={s.reverb} disabled={running} onChange={(reverb) => set({ reverb })} />
        <FxSlider label="Echo (delay)" testId="polish-delay" value={s.delay} disabled={running} onChange={(delay) => set({ delay })} />
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button type="button" data-testid="polish-apply" onClick={apply} disabled={busy || running || !changed}
          className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-5 py-2.5 text-[13px] font-semibold text-black transition-transform hover:scale-[1.03] disabled:opacity-40 disabled:hover:scale-100">
          {busy || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Re-make Studio-Polished
        </button>
        {changed && !running ? <button type="button" data-testid="polish-reset" onClick={() => setDraft(saved)} className="text-[12px] text-white/50 underline-offset-4 hover:text-white hover:underline">Undo changes</button> : null}
        {!changed && !running ? <span className="text-[12px] text-white/40">Change a setting to re-make it.</span> : null}
      </div>
    </section>
  );
}
